// Content-addressed hashing (ARCHITECTURE.md §12.5, Design Principle 4).
//
// SHA-256 is the content-addressing primitive used throughout the store:
// file content hashes drive incremental invalidation, and object hashes detect
// Navigation Index / manifest changes. `hashObject` uses a deterministic,
// key-sorted serialization so logically-equal objects hash identically
// regardless of property insertion order.

import { createHash } from "node:crypto";
import { readFile } from "node:fs/promises";

/** SHA-256 hex digest of a UTF-8 string. */
export function hashContent(content: string): string {
  return createHash("sha256").update(content, "utf8").digest("hex");
}

/** Read a file from disk and return the SHA-256 hex digest of its content. */
export async function hashFile(filePath: string): Promise<string> {
  const content = await readFile(filePath, "utf8");
  return hashContent(content);
}

/**
 * Deterministic SHA-256 of an arbitrary JSON-serializable value. Object keys
 * are sorted recursively so that property order does not affect the hash;
 * array order is preserved. `undefined`-valued properties are omitted, matching
 * JSON.stringify semantics.
 */
export function hashObject(obj: unknown): string {
  return hashContent(stableStringify(obj));
}

function stableStringify(value: unknown): string {
  if (value === null) return "null";

  const type = typeof value;
  if (type === "number" || type === "boolean") return JSON.stringify(value);
  if (type === "string") return JSON.stringify(value);
  if (type === "bigint") return JSON.stringify((value as bigint).toString());
  if (type === "undefined" || type === "function") return "null";

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries
    .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`)
    .join(",")}}`;
}
